import { I18nService } from '../../i18n/I18nService';
import { I18N_KEYS } from '../../i18n/keys';
import { FocusTrap } from '../../accessibility/FocusTrap';

/**
 * Terms of Service modal.
 * Opens from footer "Terms of Service" / "Điều khoản sử dụng" link.
 * Supports Vietnamese & English via i18n. Accessible: ESC to close, focus trap.
 */
export class TermsModal {
  private i18n: I18nService;
  private focusTrap: FocusTrap;
  private dialog: HTMLDialogElement | null = null;

  constructor(i18n: I18nService, focusTrap: FocusTrap) {
    this.i18n = i18n;
    this.focusTrap = focusTrap;
  }

  render(): void {
    this.dialog = document.getElementById('terms-modal') as HTMLDialogElement;
    if (!this.dialog) {
      this.dialog = document.createElement('dialog');
      this.dialog.id = 'terms-modal';
      this.dialog.className = 'modal modal--terms';
      this.dialog.setAttribute('aria-labelledby', 'terms-modal-title');
      document.body.appendChild(this.dialog);
    }
  }

  open(): void {
    if (!this.dialog) this.render();

    // Format multiline content into paragraphs
    const formatContent = (text: string): string =>
      text
        .split('\n')
        .filter((line) => line.trim())
        .map((line) => `<p>${line.trim()}</p>`)
        .join('');

    const sections = [
      { title: I18N_KEYS.TERMS_SECTION_1_TITLE, content: I18N_KEYS.TERMS_SECTION_1_CONTENT },
      { title: I18N_KEYS.TERMS_SECTION_2_TITLE, content: I18N_KEYS.TERMS_SECTION_2_CONTENT },
      { title: I18N_KEYS.TERMS_SECTION_3_TITLE, content: I18N_KEYS.TERMS_SECTION_3_CONTENT },
      { title: I18N_KEYS.TERMS_SECTION_4_TITLE, content: I18N_KEYS.TERMS_SECTION_4_CONTENT },
      { title: I18N_KEYS.TERMS_SECTION_5_TITLE, content: I18N_KEYS.TERMS_SECTION_5_CONTENT },
    ];

    const sectionsHtml = sections
      .map(
        (s) => `
        <section class="terms__section">
          <h3 class="terms__section-title">${this.i18n.t(s.title)}</h3>
          <div class="terms__text">${formatContent(this.i18n.t(s.content))}</div>
        </section>
      `
      )
      .join('');

    const closeLabel = this.i18n.t(I18N_KEYS.TERMS_CLOSE);

    this.dialog!.innerHTML = `
      <div class="modal__content">
        <div class="modal__header">
          <h2 id="terms-modal-title">${this.i18n.t(I18N_KEYS.TERMS_TITLE)}</h2>
          <button class="modal__close-btn" type="button" aria-label="${closeLabel}">&times;</button>
        </div>
        <div class="modal__body terms__body">
          ${sectionsHtml}
        </div>
        <div class="modal__footer">
          <button class="terms__close-btn btn btn--primary" type="button">${closeLabel}</button>
        </div>
      </div>
    `;

    this.dialog!.querySelector('.modal__close-btn')?.addEventListener('click', () => this.close());
    this.dialog!.querySelector('.terms__close-btn')?.addEventListener('click', () => this.close());

    this.dialog!.showModal();
    this.focusTrap.activate(this.dialog!);
  }

  close(): void {
    this.focusTrap.deactivate();
    if (this.dialog?.open) {
      this.dialog.close();
    }
  }

  isOpen(): boolean {
    return this.dialog?.open === true;
  }
}
